import React, { useState } from 'react';
import classNames from 'classnames';
import css from './SendTestEmailModal.module.css';
import Hint from '../../components/Hint/Hint';
import { Button, Spinner } from '@mm/components';
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { ModalInstanceProps } from '../interfaces';
import { EmailInput } from '../InviteUserModal/EmailInput/EmailInput';
import { randomId } from '../../helpers/utils';
import { EMAIL_REGEX_DOT_REQUIRED } from '../../constants/validations';
import { getErrorJson } from '@mm/utils';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import starsImage from '../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/Icons/stars.svg';

export interface ISendTestEmailModal {
  className?: string;
  chatbotId: number;
  title?: string;
  subtitle?: string;
  onSubmit?: (value: string[]) => Promise<void>;
}

type Props = ISendTestEmailModal & ModalInstanceProps;

const CAP = 8;
const ids = [randomId()];

export const SendTestEmailModal: React.FC<Props> = ({
  className,
  chatbotId,
  title: titleValue = 'Send me a test email',
  subtitle = 'You will receive the Master Email Template.',
  onSubmit = async () => {},
  closeModal,
}) => {
  const [title, setTitle] = useState(titleValue);
  const { openNewInfoModal } = useModalActions();

  const [error, setError] = useState<any>();

  const [emails, setEmails] = useState<string[]>(['']);

  const [busy, setBusy] = useState(false);

  const onAddEmail = () => {
    setEmails([...emails, '']);
    ids.push(randomId());
  };

  const canSubmit =
    emails.every(email =>
      EMAIL_REGEX_DOT_REQUIRED.test(email.toString().trim()),
    ) && !busy;

  const handleReject = async () => {
    closeModal();
  };

  const handleSubmit = async () => {
    try {
      setBusy(true);
      await onSubmit(emails);
      openNewInfoModal({
        image: starsImage,
        title: 'We sent you the Master Email Template',
        description: 'It might take a few minutes to arrive.',
        button: 'Close',
        css: { button: css.successButton },
      });
    } catch (err) {
      if (err instanceof Response) {
        const responseError = await getErrorJson(err);
        if (Array.isArray(responseError.errors)) {
          setError(responseError.errors[0]);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const Error = <Hint className={css.hint}>{error}</Hint>;

  const Content = (
    <>
      <h1 className={css.title}>
        {title}
        <div className={css.subtitle}>{subtitle}</div>
      </h1>
      <div className={css.block}>
        <span>Testing email:</span>

        {emails.map((email, idx) => (
          <div className={css.inputRow} key={ids[idx]}>
            <EmailInput
              strict
              withIcon={false}
              onChange={v => {
                const dup = [...emails];
                dup[idx] = v.email;
                setEmails(dup);
              }}
            />
            {!!idx && (
              <DeleteOutlined
                className={css.deleteButton}
                onClick={() => {
                  const dup = [...emails];
                  dup.splice(idx, 1);
                  ids.splice(idx, 1);
                  setEmails(dup);
                }}
              />
            )}
          </div>
        ))}
        {emails.length < CAP && (
          <Button
            css={{ container: css.addButton }}
            color="transparent-no-shadow"
            leftIcon={<PlusOutlined />}
            onClick={onAddEmail}
          >
            Add one more
          </Button>
        )}
        {emails.length >= CAP && (
          <span className={css.hint}>Up to {CAP} emails</span>
        )}
      </div>

      <div className={css.actions}>
        <Button
          onClick={handleReject}
          color="transparent-no-shadow"
          css={{ container: css.cancelButton }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          leftIcon={<SendOutlined />}
          css={{ container: css.sendButton }}
        >
          {busy ? <Spinner standalone size={13} /> : 'Send'}
        </Button>
      </div>
    </>
  );

  return (
    <div className={classNames(className, css.container)}>
      <div className={classNames(css.modal, { [css.error]: error })}>
        <div className={css.modalContent}>
          <span className={css.close} onClick={handleReject}>
            <CloseOutlined />
          </span>
          {!error && Content}
          {error && Error}
        </div>
      </div>
    </div>
  );
};
